import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { defaultApiConfig, buildUrl, buildHeaders, createApiError } from '@callbridge/ui';
import { SpadeSvg, HeartSvg, DiamondSvg, ClubSvg } from '../components/SuitIcons';
import { trackEvent } from '../lib/analytics';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { startLoading, loginSuccess, authError, isLoading, error } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    startLoading();

    try {
      const path = '/auth/login';
      const body = { email, password };
      const res = await fetch(buildUrl(defaultApiConfig.baseUrl, path), {
        method: 'POST',
        headers: buildHeaders(defaultApiConfig, { body, skipAuth: true }),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = (await res.json()) as { error?: { code: string; message: string } };
        throw createApiError(res.status, { status: 'error', error: data.error });
      }

      const json = (await res.json()) as {
        data: {
          user: {
            id: string;
            username: string;
            email: string;
            gamesPlayed: number;
            gamesWon: number;
            rating: number;
            createdAt: string;
          };
          tokens: { accessToken: string; refreshToken: string };
        };
      };

      loginSuccess(json.data.user, json.data.tokens.accessToken, json.data.tokens.refreshToken);
      trackEvent('login', { method: 'email' });
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      authError(msg);
    }
  }

  return (
    <div className="page auth-page">
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      <div className="auth-brand">
        <SpadeSvg className="brand-icon" />
        <span className="brand-text">Call Bridge</span>
      </div>

      <div className="auth-card">
        <h1>Login</h1>
        <p className="auth-subtitle">Welcome back to Call Bridge</p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <div className="form-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="auth-switch">
          Don&apos;t have an account? <Link to="/register">Register</Link>
        </p>
      </div>
    </div>
  );
}
